import * as d3 from 'd3';
import { Block } from '../types';
import {
  getDecisionVisibleCharCap,
  getProcessLikeVisibleCharCap,
  getStartEndVisibleCharCap,
  splitDecisionTextIntoTwoLines,
} from './blockUtils';
import { applyContentFitTextStyles, getContentFitTextLines, isContentFitActive } from './blockTextLayout';

function renderDecisionSvgText(element: SVGTextElement, block: Block, text: string): void {
  const textElement = d3.select(element);
  textElement.selectAll('*').remove();

  const fontSize = block.textStyle?.fontSize || 12;
  const lineHeight = fontSize * 1.2;
  const halfWidth = Number(textElement.attr('x') || 0);
  const displayBlock = { ...block, text };
  const textLines = isContentFitActive(block)
    ? getContentFitTextLines(displayBlock)
    : splitDecisionTextIntoTwoLines(
        text,
        getDecisionVisibleCharCap(block.textStyle?.fontSize, block)
      );

  textLines.forEach((line, index) => {
    if (index === 0) {
      textElement.text(line);
    } else {
      textElement.append('tspan')
        .attr('x', halfWidth)
        .attr('dy', lineHeight)
        .attr('text-anchor', 'middle')
        .text(line);
    }
  });
}

// Tronquer le texte selon le type de bloc (aligné sur le rendu des blocs)
function sliceText(text: string, block: Block): string {
  if (!text) return text;

  if (block.type === 'decision') {
    const maxLength = getDecisionVisibleCharCap(block.textStyle?.fontSize, block);
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  }
  if (block.type === 'note') {
    return text;
  }
  if (block.type === 'start' || block.type === 'end') {
    const maxLength = getStartEndVisibleCharCap(block.textStyle?.fontSize, block);
    if (text.length <= maxLength) {
      return text;
    }
    return text.slice(0, maxLength) + '...';
  }
  const maxLength = getProcessLikeVisibleCharCap(block.textStyle?.fontSize, block);
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + '...';
}

export function setupTextEditing(
  element: HTMLElement | SVGTextElement,
  block: Block,
  enabled: boolean,
  onUpdate: (id: string, newText: string) => void
) {
  if (!enabled) return;

  element.addEventListener('dblclick', (event) => {
    const e = event as MouseEvent;
    e.stopPropagation();

    // Create a container that will maintain position relative to viewport
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.zIndex = '1000';
    container.style.pointerEvents = 'none';

    // Get element position relative to viewport
    const { top, left, width, height } = element.getBoundingClientRect();

    container.style.top = `${top}px`;
    container.style.left = `${left}px`;
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;

    // Hide original text
    (element as SVGTextElement).style.visibility = 'hidden';

    const allowMultiline =
      block.type === 'note' ||
      block.bpmnSymbol === 'textAnnotation' ||
      element instanceof HTMLElement;

    // Textarea so Shift+Enter can insert new lines
    const input = document.createElement('textarea');
    input.value = block.text || '';
    input.rows = Math.max(2, (block.text || '').split('\n').length);
    input.style.width = '100%';
    input.style.height = '100%';
    input.style.position = 'absolute';
    input.style.resize = 'none';
    input.style.overflow = 'auto';
    input.style.whiteSpace = 'pre-wrap';
    input.style.wordBreak = 'break-word';
    input.style.lineHeight = block.textStyle?.lineHeight || '1.3';
    input.style.textAlign =
      block.bpmnSymbol === 'textAnnotation' || block.type === 'note'
        ? (block.textStyle?.textAlign || 'left')
        : (block.textStyle?.textAlign || 'center');
    input.style.fontSize = block.textStyle?.fontSize ? `${block.textStyle.fontSize}px` : '14px';
    input.style.fontWeight = 'normal';
    input.style.fontFamily = block.textStyle?.fontFamily || 'inherit';
    const isLightSurface =
      block.type === 'note' ||
      block.bpmnSymbol === 'textAnnotation' ||
      block.bpmnSymbol === 'dataObject' ||
      block.bpmnSymbol === 'dataStore';
    const editColor = block.textStyle?.textColor || (isLightSurface ? '#111827' : '#ffffff');
    input.style.color = editColor;
    input.style.caretColor = editColor;
    input.style.background = isLightSurface ? 'rgba(255,255,255,0.96)' : 'transparent';
    input.style.border = isLightSurface ? '1px solid #cbd5e1' : 'none';
    input.style.borderRadius = isLightSurface ? '4px' : '0';
    input.style.outline = 'none';
    input.style.padding = isLightSurface ? '2px 4px' : '0';
    input.style.margin = '0';
    input.style.boxSizing = 'border-box';
    input.style.pointerEvents = 'auto';

    // Add input to container
    container.appendChild(input);
    document.body.appendChild(container);

    // Focus and select text
    input.focus();
    input.select();

    let isSaved = false;

    const cleanup = () => {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
      (element as SVGTextElement).style.visibility = 'visible';
    };

    const saveChanges = () => {
      if (isSaved) return;
      isSaved = true;

      // Keep internal newlines; only trim outer edges
      const newText = input.value.replace(/^\s+|\s+$/g, '');
      // Permettre la sauvegarde même si le texte est vide (suppression complète)
      if (newText !== block.text) {
        // Sauvegarder le texte (peut être vide) dans l'objet block
        block.text = newText;
        
        // Pour les blocs de décision, mettre à jour avec 2 lignes si nécessaire
        if (block.type === 'decision') {
          if (element instanceof SVGTextElement) {
            renderDecisionSvgText(element, block, newText);
          }
        } else if (isContentFitActive(block) && element instanceof HTMLElement) {
          element.textContent = newText || '';
          applyContentFitTextStyles(element);
        } else if (element instanceof HTMLElement) {
          const displayedText = sliceText(newText, block);
          element.textContent = displayedText || '';
          if (allowMultiline) {
            element.style.whiteSpace = 'pre-wrap';
          }
        } else {
          // Pour les autres blocs, utiliser la troncature normale
          const displayedText = sliceText(newText, block);
          element.textContent = displayedText || '';
        }
        
        // Callback pour synchroniser les données (sans re-rendu)
        // On envoie le texte complet (même vide) pour la sauvegarde, mais l'affichage reste tronqué
        onUpdate(block.id, newText);
      } else {
        // Restaurer le texte original si pas de changement
        if (block.type === 'decision') {
          if (element instanceof SVGTextElement) {
            renderDecisionSvgText(element, block, block.text);
          }
        } else if (isContentFitActive(block) && element instanceof HTMLElement) {
          element.textContent = block.text || '';
          applyContentFitTextStyles(element);
        } else {
          const displayedText = sliceText(block.text, block);
          element.textContent = displayedText || '';
        }
      }

      cleanup();
    };

    // Handle input events
    input.addEventListener('blur', saveChanges, { once: true });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        saveChanges();
      } else if (e.key === 'Enter' && e.shiftKey) {
        // Allow default newline insert in the textarea
        e.stopPropagation();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cleanup();
      }
    });

    // Update container position on scroll
    const updatePosition = () => {
      if (!isSaved) {
        const rect = element.getBoundingClientRect();
        container.style.top = `${rect.top}px`;
        container.style.left = `${rect.left}px`;
      }
    };

    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    // Cleanup event listeners when done
    const cleanupEvents = () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };

    input.addEventListener('blur', cleanupEvents, { once: true });
  });
}




